import { create } from 'zustand';
import {
   listSessions,
   ApiVendoSession,
   DataSessionUsage,
   ListSessionsParams,
   SessionEntry,
   TimeSessionUsage,
} from '@/api/vendo';
import { noteServerTime } from '@/utils/serverClock';

/** The window of finished sessions the Sessions screen is showing. */
export type HistoryWindow = Omit<ListSessionsParams, 'view'>;

interface State {
   // LIVE sessions only (active + paused + Unlimited rows). Finished ones are
   // never polled for the whole list any more: the 10 s poll from every open
   // app was api.smartfi.ph's top load (2026-10-26).
   sessions: ApiVendoSession[];
   // The page of finished sessions the Sessions screen asked for, and how
   // many match in all. Empty while nobody is looking at finished sessions.
   history: HistoryWindow | null;
   historyRows: ApiVendoSession[];
   historyTotal: number;
   // Sessions started today (PH), keyed by vendo _id — the Overview card.
   todayByVendo: Record<string, number>;
   // Live byte counters for active data-rate sessions, keyed by
   // session._id. Updated on every fetch alongside `sessions`.
   dataUsageBySessionId: Record<string, DataSessionUsage>;
   pauseWindowBySessionId: Record<string, number | null>;
   // What went into a session that holds more than one entry.
   entriesBySessionId: Record<string, SessionEntry[]>;
   // Live byte counters + throughput for active TIME sessions —
   // pulled from /queue/simple stats by the server.
   timeUsageBySessionId: Record<string, TimeSessionUsage>;
   loading: boolean;
   error: string | null;
   fetched: boolean;
   fetch: () => Promise<void>;
   /** Ask for a window of finished sessions (null = none); fetches at once. */
   setHistory: (w: HistoryWindow | null) => void;
}

const sameWindow = (a: HistoryWindow | null, b: HistoryWindow | null) =>
   JSON.stringify(a) === JSON.stringify(b);

export const useVendoSessionsStore = create<State>((set, get) => ({
   sessions: [],
   history: null,
   historyRows: [],
   historyTotal: 0,
   todayByVendo: {},
   dataUsageBySessionId: {},
   pauseWindowBySessionId: {},
   entriesBySessionId: {},
   timeUsageBySessionId: {},
   loading: false,
   error: null,
   fetched: false,
   fetch: async () => {
      set({ loading: true, error: null });
      const window = get().history;
      try {
         const [res, hist] = await Promise.all([
            listSessions({ view: 'live' }),
            window ? listSessions({ view: 'history', ...window }) : Promise.resolve(null),
         ]);
         // Re-anchor the session countdown to the server's clock on every
         // poll (10s), so a viewing device with the wrong date/time still
         // shows the customer's real time-left. Cheap, and this is the one
         // place every sessions fetch lands — dashboard and sub-login both.
         noteServerTime(res);
         // The operator may have paged on while this was in flight: a reply
         // for a window no longer on screen must not overwrite the new one.
         const stillWanted = sameWindow(window, get().history);
         set({
            sessions: res.sessions,
            ...(stillWanted
               ? { historyRows: hist?.sessions ?? [], historyTotal: hist?.historyTotal ?? 0 }
               : {}),
            todayByVendo: res.todayByVendo || {},
            dataUsageBySessionId: { ...(hist?.dataUsageBySessionId || {}), ...(res.dataUsageBySessionId || {}) },
            pauseWindowBySessionId: res.pauseWindowBySessionId || {},
            entriesBySessionId: { ...(hist?.entriesBySessionId || {}), ...(res.entriesBySessionId || {}) },
            timeUsageBySessionId: res.timeUsageBySessionId || {},
            loading: false,
            fetched: true,
         });
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            (err as Error)?.message ||
            'Failed to load sessions';
         set({ error: msg, loading: false, fetched: true });
      }
   },
   setHistory: (w) => {
      if (sameWindow(w, get().history)) return;
      set(w ? { history: w } : { history: null, historyRows: [], historyTotal: 0 });
      if (w) void get().fetch();
   },
}));
